import React, {
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import IconClose from "../../../../dynamicicons/icon-close";
import IconPlace from "../../../../dynamicicons/icon-place";
import VelavuAutocomplete, {
	AutocompleteDivider,
} from "../../../../elements/velavu-autocomplete";
import { useDebouncedEffect } from "../../../../helper/hook-helper";
import { useSignInContext } from "../../../sign-in-context/sign-in-context";
import * as styles from "./site-create-search.module.scss";
import whitelabel from "../../../../whitelabel";
import { mapboxForwardGeocode } from "../../../../helper/mapbox-geocoding-helper";

export interface SiteCreateAddressResult {
	id: string;
	position: [number, number];
	name: string;
}

interface SiteCreateSearchProps {
	address: SiteCreateAddressResult | undefined;
	onSetAddress: (address: SiteCreateAddressResult | undefined) => void;
	from: string;
	disabled?: boolean;
}

export default function SiteCreateSearch(props: SiteCreateSearchProps) {
	const { i18n } = useTranslation();
	const [inputSiteAddress, setInputSiteAddress] = useState("");
	const [searchResults, setSearchResults] = useState<
		SiteCreateAddressResult[] | undefined
	>(undefined);
	const user = useSignInContext();
	const refInput = useRef<HTMLInputElement | null>(null);

	const updateSearch = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const value = event.target.value;
			setInputSiteAddress(value);
		},
		[setInputSiteAddress],
	);

	const mapboxToken = useMemo(
		() => user.velavuUser?.mapbox_token,
		[user.velavuUser],
	);

	let mapboxLanguage: string;
	if (i18n.language === "en") {
		// For some reason, geocoding with 'en' yields no results,
		// but 'en-US' does.
		mapboxLanguage = "en-US";
	} else {
		mapboxLanguage = i18n.language;
	}

	useDebouncedEffect(
		() => {
			//Ignoring if there is no query
			if (inputSiteAddress.trim() === "") {
				setSearchResults(undefined);
				return;
			}

			//Getting the mapbox token
			if (mapboxToken === undefined) {
				console.warn("Geocoder failed; no Mapbox token");
				return;
			}

			//Perform the geocoding request
			let isMounted = true;
			mapboxForwardGeocode(mapboxToken, inputSiteAddress, {
				language: mapboxLanguage,
				types: "address",
				proximity: "ip",
			}).then((response) => {
				if (!isMounted) {
					return;
				}

				setSearchResults(
					response.features.map(
						(feature): SiteCreateAddressResult => ({
							id: feature.id,
							position: [
								feature.properties.coordinates.longitude,
								feature.properties.coordinates.latitude,
							],
							name: feature.properties.full_address,
						}),
					),
				);
			});

			return () => {
				isMounted = false;
			};
		},
		300,
		false,
		[
			inputSiteAddress,
			setSearchResults,
			mapboxToken,
			mapboxLanguage,
			props.from,
		],
	);

	return (
		<VelavuAutocomplete
			ref={refInput}
			inputProps={{
				value: inputSiteAddress,
				onChange: updateSearch,
				disabled: props.disabled,
			}}
			contentOverride={
				!props.address ? undefined : (
					<div
						className={styles.selectionDisplay}
						onClick={
							props.disabled
								? undefined
								: () => {
										props.onSetAddress(undefined);
										setTimeout(() =>
											refInput.current!.focus(),
										);
									}
						}
					>
						<span className={styles.selectionDisplayLabel}>
							{props.address.name}
						</span>
						{!props.disabled && (
							<IconClose
								color={whitelabel.palette.secondary500}
								size={16}
							/>
						)}
					</div>
				)
			}
			disableSuggestions={props.address !== undefined || props.disabled}
		>
			{searchResults !== undefined && searchResults.length > 0 && (
				<>
					<AutocompleteDivider />
					{searchResults.map((result) => (
						<AddressEntry
							key={result.id}
							address={result}
							onClick={() => {
								props.onSetAddress(result);
								setSearchResults(undefined);
								setInputSiteAddress("");
							}}
						/>
					))}
				</>
			)}
		</VelavuAutocomplete>
	);
}

function AddressEntry(props: {
	address: SiteCreateAddressResult;
	onClick?: VoidFunction;
}) {
	return (
		<button className={styles.addressEntry} onClick={props.onClick}>
			<IconPlace
				className={styles.addressEntryIcon}
				color={whitelabel.palette.secondary700}
				size={16}
			/>
			<span className={styles.addressEntryLabel}>
				{props.address.name}
			</span>
		</button>
	);
}
